<template>
  <aside
    class="is-3 p-[0.75rem] z-10 lg:pl-0"
    aria-label="Product Filters"
  >
    <template v-if="visibleFacets.length > 0">
      <template v-for="facetKey in visibleFacets" :key="facetKey">
        <section
          v-if="facetKey === 'Ingredients'"
          class="mb-[2.8571428571rem]"
          aria-label="Ingredients"
          :key="`ingredients:${props.facetsUiVersion}`"
        >
          <div class="flex items-center justify-between mb-[1.5rem]">
            <Heading :level="5" color="text-ash-200">{{ facetKey }}</Heading>
            <NuxtLink
              :to="URL_CONFIG.INGREDIENTS"
              id="see-all-ingredients-link"
              title="See all ingredients"
              aria-label="See all ingredients"
              external
              class="text-ash-500 hover:text-gold-100 underline"
            >
              See All
            </NuxtLink>
          </div>

          <hr class="border-ash-300 mb-[1.5rem]" />

          <AutoCompleteInput
          :key="`ingredients-input:${props.facetsUiVersion}`"
          :modelValue="props.selectedFilters[facetKey] ?? []"
          @update:modelValue="(values) => props.setFacetValues('Ingredients', values)"
          :options="
            (facets[facetKey]?.values ?? [])
              .filter((facetValue: FacetsValue) => facetValue.count !== 0)
              .map((facetValue: FacetsValue) => facetValue.value)
          "
          @dropdown-closed="dropdownClosed()"
          placeholder="Search ingredients"
        />
        </section>

        <!-- Other checkbox facets -->
        <FacetCheckbox
          v-else
          :key="`${facetKey}:${props.facetsUiVersion}`"
          :title="facetKey"
          :options="facets[facetKey]?.values ?? []"
          :visibleCount="facetKey === 'Health Needs' ? 6 : undefined"
          :modelValue="props.selectedFilters[facetKey] ?? []"
          @update:modelValue="(values) => handleFacetChange(facetKey, values)"
          :index="Object.keys(facets).indexOf(facetKey)"
        />
      </template>
    </template>
  </aside>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'
import Heading from '../../atom/heading.vue'
import AutoCompleteInput from '../../organism/product/autoCompleteInput.vue'
import FacetCheckbox from '../../organism/product/facetCheckbox.vue'
import {URL_CONFIG} from '~~/shared/utils/url.const'
import type {Facet, FacetDict, FacetsValue} from '~~/shared/types/common'

const props = defineProps<{
  facets: FacetDict
  selectedFilters: Record<string, string[]>
  facetsUiVersion: number
  error: string | null
  setFacetValues: (facetName: string, values: string[]) => void
  clearAllFacets: () => void
}>()

const visibleFacets = computed(()=> {
  const facetList: FacetDict = props.facets
  return Object.keys(facetList).filter(facetKey => {
    let containsValuesWithCount = false
    const facet: Facet | undefined = facetList[facetKey]
    const facetValues: FacetsValue[]|undefined = facet?.values

    if (facetValues) {
      containsValuesWithCount = facetValues.some(value => value.count > 0)
    }

    return containsValuesWithCount
  });
})

const handleFacetChange = (facetName: string, values: string[]) => {
  props.setFacetValues(facetName, values)
}

const dropdownClosed = () => {
 const inputEl = document.getElementById("ingredient-input")
  if (!inputEl) return

  const allCheckboxes = Array.from(document.querySelectorAll<HTMLInputElement>("input[type='checkbox']"))

  const nextCheckbox = allCheckboxes.find(cb =>
    inputEl.compareDocumentPosition(cb) & Node.DOCUMENT_POSITION_FOLLOWING
  )
    
  if (nextCheckbox && !nextCheckbox.disabled) {
    nextTick(() => { 
      nextCheckbox.focus() 
    })
  }
}

</script>
